import { Directive, ElementRef, Input, OnInit, Renderer2, HostListener } from '@angular/core';
import { CommonService } from '@app/core';
import { featureDetails } from '@app/models';

@Directive({
    selector: '[appFeatureAccess]',
    standalone: false
})
export class FeatureAccessDirective implements OnInit {
  @Input('appFeatureAccess') featureName!: string;
  @Input() allowedAccessLevels: string[] = [];
  @Input() hideIfNoAccess = false;

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly commonService: CommonService
  ) {}

  ngOnInit() {
    this.checkFeatureAccess();
  }

  private checkFeatureAccess() {
    const userFeatures: featureDetails[] = this.commonService.appProfileDataAsValue.user.features;
    const feature = userFeatures.find(f => f.featureName === this.featureName);

    if (feature && this.allowedAccessLevels.includes(feature.access_level)) {
      this.enableElement();
    } else {
        if (this.hideIfNoAccess) {
          this.renderer.removeChild(
            this.el.nativeElement.parentNode,
            this.el.nativeElement
          );
        } else {
          this.disableElement();
        }
    }
  }

  private enableElement() {
    this.renderer.setStyle(this.el.nativeElement, 'pointer-events', 'auto');
    this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
    this.renderer.setAttribute(this.el.nativeElement, 'aria-disabled', 'false');
  }

  private disableElement() {
    this.renderer.setStyle(this.el.nativeElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.el.nativeElement, 'opacity', '0.5');
    this.renderer.setAttribute(this.el.nativeElement, 'aria-disabled', 'true');

    const tagName = this.el.nativeElement.tagName.toLowerCase();

    // Handle specific element types
    if (tagName === 'a') {
        this.disableAnchorTag();
      } else if (tagName === 'table') {
      this.disableTableElements();
    } else if (tagName === 'form') {
      this.disableFormControls();
    } else if (tagName === 'tr') {
      this.disableRowCells();
    } else if (tagName === 'div' || tagName === 'mat-expansion-panel') {
      this.disableDivElements();
    } else if (tagName === 'textarea') {
      this.disableTextarea();
    }
  }

  private disableAnchorTag() {
    this.renderer.removeAttribute(this.el.nativeElement, 'href');
    this.renderer.setStyle(this.el.nativeElement, 'cursor', 'not-allowed');
  }

  private disableTableElements() {
    const rows = this.el.nativeElement.querySelectorAll('tr');
    rows.forEach((row: HTMLElement) => {
      this.renderer.setStyle(row, 'pointer-events', 'none');
      this.renderer.setStyle(row, 'opacity', '0.5');
      Array.from(row.children).forEach((cell) => {
        if (cell instanceof HTMLElement) {
          this.renderer.setStyle(cell, 'pointer-events', 'none');
        }
      });
    });
  }

  private disableFormControls() {
    const formElements = this.el.nativeElement.querySelectorAll('input, select, textarea, button');
    formElements.forEach((field: HTMLElement) => {
      this.renderer.setProperty(field, 'disabled', true);
    });
  }

  private disableRowCells() {
    Array.from(this.el.nativeElement.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        this.renderer.setStyle(child, 'pointer-events', 'none');
      }
    });
  }

  private disableDivElements() {
    const divs = this.el.nativeElement.querySelectorAll('div');
    divs.forEach((div: HTMLElement) => {
      this.renderer.setStyle(div, 'pointer-events', 'none');
      this.renderer.setStyle(div, 'opacity', '0.5');
    });
  }

  private disableTextarea() {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', true);
    this.renderer.setStyle(this.el.nativeElement, 'opacity', '0.5');
  }

  // Host listener to prevent default action on clicks within disabled elements
  @HostListener('click', ['$event'])
  onClick(event: Event) {
    if (this.el.nativeElement.getAttribute('aria-disabled') === 'true') {
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
    return true;
  }
}